import React from 'react';
import styled from 'styled-components';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'destructive' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const StyledBadge = styled.span<{
  $variant: BadgeProps['variant'];
  $size: BadgeProps['size'];
  $clickable: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: var(--radius);
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1;
  white-space: nowrap;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${props => {
    const size = props.$size || 'md';
    const variant = props.$variant || 'default';
    
    const sizeStyles = {
      sm: 'padding: 0.125rem 0.375rem; font-size: 0.625rem;',
      md: 'padding: 0.25rem 0.5rem; font-size: 0.75rem;',
      lg: 'padding: 0.375rem 0.75rem; font-size: 0.875rem;'
    };
    
    const variantStyles = {
      default: `
        background-color: var(--primary);
        color: var(--primary-foreground);
      `,
      primary: `
        background-color: var(--primary);
        color: var(--primary-foreground);
      `,
      secondary: `
        background-color: var(--secondary);
        color: var(--secondary-foreground);
      `,
      outline: `
        background-color: transparent;
        color: var(--foreground);
        border: 1px solid var(--border);
      `,
      destructive: `
        background-color: var(--destructive);
        color: var(--destructive-foreground);
      `,
      success: `
        background-color: var(--success);
        color: var(--success-foreground);
      `,
      warning: `
        background-color: var(--warning);
        color: var(--warning-foreground);
      `
    };
    
    return `
      ${sizeStyles[size]}
      ${variantStyles[variant]}
      
      ${props.$clickable ? `
        &:hover {
          opacity: 0.8;
          transform: translateY(-1px);
        }
      ` : ''}
    `;
  }}
`;

const Badge: React.FC<BadgeProps> = ({ 
  children, 
  variant = 'default', 
  size = 'md',
  onClick,
  style,
  ...props 
}) => {
  return (
    <StyledBadge
      $variant={variant}
      $size={size}
      $clickable={!!onClick}
      onClick={onClick}
      style={style}
      {...props}
    >
      {children}
    </StyledBadge>
  );
};

export default Badge; 